import { PRODUCTS } from '@/utilities/constants/game';
import { useStore } from '@/store/useStore';

const getRuntimeProduct = () => {
  try {
    return (
      useStore.getState?.().runtime?.product ?? process.env.PRODUCT ?? 'casino'
    );
  } catch {
    return process.env.PRODUCT ?? 'casino';
  }
};

const PRODUCT =
  getRuntimeProduct()?.toLowerCase() === PRODUCTS.POKER ? 'poker' : 'casino';

export const POPUP_TYPES = {
  PROVIDER: 'PROVIDER_ERROR',
  EXIT: 'EXIT_POPUP',
  EXIT_RECENTLY: 'EXIT_POPUP_RECENTLY_PLAYED',
  SWJ: 'SWJ_POPUP',
  REALITY_CHECK: 'REALITY_CHECK_POPUP',
  JACKPOT_WIN: 'JACKPOT_WIN',
  IN_GAME_MERCH: 'IN_GAME_MERCH',
};

export const DYNAMIC_POPUP_TYPES = {
  GEOLOCATION_REFRESH: 'GEOLOCATION_REFRESH',
};

export enum GEOLOCATION_STEPS {
  LOADING = 'geolocation_loading',
  ERROR = 'geolocation_error',
  SUCCESS = 'geolocation_success',
}

export const CLOSABLE_POPUPS = [DYNAMIC_POPUP_TYPES.GEOLOCATION_REFRESH];

export const CUSTOM_SIZES = { SMALL: 'small' } as const;

export const ERROR_TYPE = {
  GENERIC: 'catch_all',
  GENERIC_DISMISSABLE: 'generic_dismissable',
  INSUFFICIENT_FUNDS: 'balance_too_low',
  SESSION_EXPIRED: 'session_expired',
  GAME_IN_PROGRESS: 'game_in_progress',
  GAME_STOPPED_WORKING: 'game_stopped_working',
  CONNECTION_LOST: 'connection_lost',
  TOO_MANY_GAMES_OPEN: 'too_many_games_open',
  UNKNOWN: 'unknown_error_nyx',
  FREE_SPIN_DIALOG_START: 'free_spin_dialog_start',
  FREE_SPIN_DIALOG_CHOOSE: 'free_spin_dialog_choose',
  FREE_SPIN_DIALOG_FINISH: 'free_spin_dialog_finish',
  GENERAL_ERROR: 'general_error',
  FAILED_TO_REDIRECT: 'failed_to_redirect',
  FAILED_TO_FETCH_GAME: 'failed_to_fetch_game',
  NET_POSITION_SIMPLE: 'net_position_simple',
} as const;

export const SECONDARY_POPUP_TYPES = {
  GENERIC: 'generic_secondary',
  INSUFFICIENT_FUNDS: 'insufficient_funds_secondary',
} as const;

export const BUTTON_TYPE = {
  TRY_AGAIN: 'try_again',
  DEPOSIT: 'deposit',
  GO_TO_LOBBY: 'go_to_lobby',
  EXIT_GAME: 'exit_game',
  DISMISS: 'dismiss',
  SUPPORT: 'support',
  DEPOSIT_LIMITS: 'deposit_limits',
  BACK_TO_LAST_GAME: 'back_to_last_game',
  FREE_SPIN_CHOOSE_FREE_SPIN: 'free_spin_choose_free_spin',
  FREE_SPIN_CHOOSE_REAL_MONEY: 'free_spin_choose_real_money',
  FREE_SPIN_FINISH_SPINS: 'free_spin_finish_spins',
  FREE_SPIN_CHOOSE_OPTION: 'free_spin_choose_option',
  CONTINUE_WITHOUT_JACKPOTS: 'continue_without_jackpots',
  DISMISS_REALITY_CHECK: 'dismiss_reality_check',
  REDIRECT_REALITY_CHECK_URL: 'redirect_reality_check_url',
  EXIT_GAME_RECENTLY_PLAYED: 'exit_game_recently_played',
  GENERAL_ERROR_DISMISS: 'general_error_dismiss',
} as const;

export const ERROR_MESSAGES = {
  [ERROR_TYPE.GENERIC]: {
    main: {
      popupMessage:
        "We're sorry about this. Please try again or leave your game to go home.",
      popupName: `Something went wrong in the ${PRODUCT} game`,
    },
    secondary: {
      popupName: 'Leave your game to contact Support?',
      firstMessage: 'Need help? ',
      actionMessage: 'Contact our Support Team.',
    },
  },
  [ERROR_TYPE.INSUFFICIENT_FUNDS]: {
    main: {
      popupMessage:
        'Please lower your bet amount or add additional funds to continue',
      popupName: 'Insufficient Balance',
    },
    secondary: {
      popupName: 'Leave your game to set deposit limits?',
      firstMessage: 'Also, you can manage your play by ',
      actionMessage: 'setting deposit limits.',
    },
  },
  [ERROR_TYPE.SESSION_EXPIRED]: {
    main: {
      popupMessage: 'Dismiss this message to re-launch the game.',
      popupName: 'Expired session',
    },
  },
  [ERROR_TYPE.GAME_IN_PROGRESS]: {
    main: {
      popupMessage:
        'You have another game open. Please finish your last game before starting a new one.',
      popupName: 'Finish your last game',
      loadingMessage: 'Loading your last game...',
    },
  },
  [ERROR_TYPE.GAME_STOPPED_WORKING]: {
    main: {
      popupMessage:
        'Sorry, we’re working on a fix. Please go back home and try again later.',
      popupName: 'Your game stopped loading',
    },
  },
  [ERROR_TYPE.CONNECTION_LOST]: {
    main: {
      popupMessage: 'It’s not you, it’s us. Please try again or go back home.',
      popupName: 'We lost connection',
    },
  },
  [ERROR_TYPE.TOO_MANY_GAMES_OPEN]: {
    main: {
      popupMessage:
        'Please leave all of your games (on all devices) to start playing again.',
      popupName: 'Too many games open',
    },
  },
  [ERROR_TYPE.FREE_SPIN_DIALOG_START]: {
    main: {
      popupMessage: 'Choose Cash mode or Free Spins mode to play the game.',
      popupName: 'Choose one to continue',
    },
  },
  [ERROR_TYPE.FREE_SPIN_DIALOG_CHOOSE]: {
    main: {
      popupMessage: 'How would you like to use your Free Rounds?',
      popupName: 'Choose one to continue',
    },
  },
  [ERROR_TYPE.FREE_SPIN_DIALOG_FINISH]: {
    main: {
      popupMessage: 'Future rounds will be played in cash mode.',
      popupName: 'Free Spins',
    },
  },
  [ERROR_TYPE.UNKNOWN]: {
    main: {
      popupMessage:
        'We’re sorry about this. You can continue playing now or go back home.',
      popupName: `Something went wrong in the ${PRODUCT} game`,
    },
  },
  [ERROR_TYPE.GENERIC_DISMISSABLE]: {
    main: {
      popupMessage: 'Dismiss the error if you want to continue',
      popupName: `Error`,
    },
  },
} as const;

export const BUTTON_MESSAGES = {
  TRY_AGAIN: 'Try again',
  GO_HOME: 'Go to lobby',
  DEPOSIT: 'Add funds to balance',
  BACK_TO_GAME: 'Back to game',
  DISMISS: 'Dismiss',
  LEAVE_TO_SUPPORT: 'Yes, leave',
  BACK_TO_LAST_GAME: 'Back to last game',
  LEAVE_TO_GO_HOME: 'Leave to go home',
  CONTINUE_PLAYING: 'Continue playing',
  FREE_SPIN_CHOOSE_FREE_SPIN: 'Free Spins mode',
  FREE_SPIN_CHOOSE_REAL_MONEY: 'Cash mode',
  FREE_SPIN_FINISH_SPINS: 'Play for Real',
  CONTINUE_WITHOUT_JACKPOTS: 'Continue without jackpots',
  EXIT_GAME: 'Exit game',
  CANCEL: 'Cancel',
  EXIT_GAME_RECENTLY_PLAYED: 'Launch new game',
  GENERAL_ERROR_DISMISS: 'Close',
} as const;

export const BUTTON_COLORS = {
  MAIN: 'main',
  SECONDARY: 'secondary',
  BLUE: 'blue',
} as const;

/**
 * Datadog tracking events for the GEOLOCATION_REFRESH popup steps. Mirrors the
 * legacy launcher `GrTrackingEvent` enum so dashboards stay consistent.
 */
export const GR_TRACKING_EVENT = {
  INVALIDATED_MODAL_VIEWED: 'invalidated_modal_viewed',
  INVALIDATED_MODAL_LEAVE: 'invalidated_modal_leave_click',
  SUCCESS_MODAL_VIEWED: 'success_modal_viewed',
  SUCCESS_MODAL_LEAVE: 'success_modal_leave_click',
  FAIL_MODAL_VIEWED: 'fail_modal_viewed',
  FAIL_MODAL_LEAVE: 'fail_modal_leave_click',
} as const;

export const POPUP_TEST_IDS = {
  EXIT_GAME_CONFIRM_BUTTON: 'exit-game-confirm-button',
  EXIT_GAME_CANCEL_BUTTON: 'exit-game-cancel-button',
  LAUNCH_GAME_BUTTON: 'launch-game-button',
  RECENTLY_PLAYED_CANCEL_BUTTON: 'recently-played-cancel-button',
  CONTINUE_WITHOUT_JACKPOTS_BUTTON: 'continue-without-jackpots-button',
  SWJ_EXIT_GAME_BUTTON: 'swj-exit-game-button',
  REALITY_CHECK_GOT_IT_BUTTON: 'reality-check-got-it-button',
  REALITY_CHECK_EDIT_ALERTS_BUTTON: 'reality-check-edit-alerts-button',
  ADD_FUNDS_TO_BALANCE_BUTTON: 'add-funds-to-balance-button',
  DISMISS_ERROR_MODAL_BUTTON: 'dismiss-error-modal-button',
  YES_LEAVE_CONTACT_SUPPORT_BUTTON: 'yes-leave-contact-support-button',
  ERROR_FOOTER_TEXT: 'error-footer-text',
  CONTACT_SUPPORT_LINK: 'contact-support-link',
  INSUFFICIENT_BALANCE_FOOTER_TEXT: 'insufficient-balance-footer-text',
  INSUFFICIENT_BALANCE_FOOTER_LINK: 'insufficient-balance-footer-link',
} as const;

export const ERROR_CTA_EVENTS = {
  TRY_AGAIN: 'try_again',
  ADD_FUNDS: 'add_funds',
  EXIT_TOLOBBY: 'exit_to_lobby',
  DISMISS: 'back_to_game',
  CUSTOMER_SUPPORT: 'contact_our_support_team',
  SET_DEPOSIT_LIMIT: 'setting_deposit_limits',
  LEAVE: 'yes_leave',
  CONTINUE_WITHOUT_JACKPOTS: 'continue_without_jackpots',
} as const;
